(() => {
  'use strict';

  const GLOBAL_KEY = '__pbSnapCartA11y';
  const ROOT_SELECTOR = '#w4root';
  const DRAWER_SELECTOR = '#wizz4-cart-drawer';
  const STOREFRONT_SELECTOR = '#wizz4-cart-drawer > #w4-storefront-drawer';
  const HEADER_SELECTOR = '#wizz4-cart-drawer > #w4-storefront-drawer > .w4-cart-head';
  const CLOSE_SELECTOR = '#wizz4-cart-drawer > #w4-storefront-drawer > .w4-cart-head > .wizz-header-icon-custom';
  const DRAWER_ID = 'wizz4-cart-drawer';
  const VISIBILITY_EVENT = 'wizz:cart-drawer-visibility';
  const STRUCTURE_SELECTORS = [DRAWER_SELECTOR, STOREFRONT_SELECTOR, HEADER_SELECTOR, CLOSE_SELECTOR];
  const RECONCILE_ATTRIBUTES = new Set([
    'class',
    'aria-hidden',
    'aria-modal',
    'aria-label',
    'role',
    'inert',
    'tabindex',
    'aria-controls'
  ]);
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'iframe',
    'object',
    'embed',
    '[contenteditable="true"]',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const previous = window[GLOBAL_KEY];
  if (previous && typeof previous.destroy === 'function') previous.destroy();

  const readLabel = (value, fallback) => (
    typeof value === 'string' && value.trim() ? value : fallback
  );
  const config = window.__pbSnapCartA11yConfig || {};
  const closeLabel = readLabel(config.closeLabel, 'Close');
  const cartLabel = readLabel(config.cartLabel, 'Cart');

  const defer = (callback) => {
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(callback);
    } else {
      Promise.resolve().then(callback);
    }
  };

  const createController = () => {
    let mounted = false;
    let currentRoot = null;
    let currentDrawer = null;
    let currentClose = null;
    let rootObserver = null;
    let discoveryObserver = null;
    let closeCleanup = null;
    let scheduledFocusFrame = null;
    let reconcileQueued = false;
    let previousVisible = false;
    let lastTrigger = null;
    const cleanups = [];

    const on = (target, type, handler, options) => {
      if (!target || typeof target.addEventListener !== 'function') return;
      target.addEventListener(type, handler, options);
      cleanups.push(() => target.removeEventListener(type, handler, options));
    };

    const isConnected = (element) => Boolean(
      element &&
      typeof element.focus === 'function' &&
      element.isConnected !== false
    );

    const hasAttribute = (element, name) => Boolean(
      element && (
        typeof element.hasAttribute === 'function'
          ? element.hasAttribute(name)
          : element.getAttribute(name) !== null
      )
    );

    const removeAttribute = (element, name) => {
      if (!element) return;
      if (typeof element.removeAttribute === 'function') element.removeAttribute(name);
      else if (element.attributes && typeof element.attributes.delete === 'function') element.attributes.delete(name);
    };

    const isVisible = (drawer) => Boolean(
      drawer &&
      drawer.classList &&
      drawer.classList.contains('wizz4-visible')
    );

    const getUnique = (selector) => {
      if (!document.querySelectorAll) {
        return document.querySelector ? document.querySelector(selector) : null;
      }
      const matches = document.querySelectorAll(selector);
      return matches.length === 1 ? matches[0] : null;
    };

    const getRoot = () => getUnique(ROOT_SELECTOR);

    const getDrawer = (root) => {
      const drawer = getUnique(DRAWER_SELECTOR);
      return root && drawer && root.contains(drawer) ? drawer : null;
    };

    const getClose = (drawer) => {
      const close = getUnique(CLOSE_SELECTOR);
      return close && drawer && drawer.contains(close) ? close : null;
    };

    const focusElement = (element) => {
      if (!isConnected(element)) return;
      try {
        element.focus({ preventScroll: true });
      } catch (_error) {
        element.focus();
      }
    };

    const cancelScheduledFocus = () => {
      if (scheduledFocusFrame === null) return;
      if (typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(scheduledFocusFrame);
      }
      scheduledFocusFrame = null;
    };

    const scheduleFocus = (element) => {
      cancelScheduledFocus();
      const focus = () => {
        scheduledFocusFrame = null;
        if (mounted) focusElement(element);
      };
      if (typeof window.requestAnimationFrame === 'function') {
        scheduledFocusFrame = window.requestAnimationFrame(focus);
      } else {
        focus();
      }
    };

    const rememberTrigger = (drawer) => {
      const active = document.activeElement;
      if (
        active &&
        active !== document.body &&
        !drawer.contains(active) &&
        isConnected(active)
      ) {
        lastTrigger = active;
      }
    };

    const restoreTrigger = () => {
      const trigger = lastTrigger;
      lastTrigger = null;
      if (isConnected(trigger)) scheduleFocus(trigger);
    };

    const removeCloseBinding = () => {
      if (closeCleanup) closeCleanup();
      closeCleanup = null;
      currentClose = null;
    };

    const bindClose = (drawer) => {
      const close = getClose(drawer);
      if (close !== currentClose) {
        removeCloseBinding();
        currentClose = close;
        if (close) {
          const onCloseKeydown = (event) => {
            const isActivation = event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar';
            if (!isActivation) return;
            event.preventDefault();
            event.stopPropagation();
            event.currentTarget.click();
          };
          close.addEventListener('keydown', onCloseKeydown);
          closeCleanup = () => close.removeEventListener('keydown', onCloseKeydown);
        }
      }

      if (!close) return null;
      if (close.getAttribute('role') !== 'button') close.setAttribute('role', 'button');
      if (close.getAttribute('tabindex') !== '0') close.setAttribute('tabindex', '0');
      if (close.getAttribute('aria-label') !== closeLabel) close.setAttribute('aria-label', closeLabel);
      const drawerId = drawer.id || DRAWER_ID;
      if (close.getAttribute('aria-controls') !== drawerId) close.setAttribute('aria-controls', drawerId);
      return close;
    };

    const updateDialog = (drawer, visible) => {
      if (!drawer) return;
      if (drawer.getAttribute('role') !== 'dialog') drawer.setAttribute('role', 'dialog');
      if (visible) {
        // Remove inert before exposing the drawer to assistive technology. The vendor owns
        // visibility; this only keeps the accessibility state synchronized with its class.
        if (hasAttribute(drawer, 'inert')) removeAttribute(drawer, 'inert');
        if (drawer.getAttribute('aria-modal') !== 'true') drawer.setAttribute('aria-modal', 'true');
        if (drawer.getAttribute('aria-label') !== cartLabel) drawer.setAttribute('aria-label', cartLabel);
        if (drawer.getAttribute('aria-hidden') !== 'false') drawer.setAttribute('aria-hidden', 'false');
        return;
      }

      // A hidden dialog must be removed from sequential focus navigation as well as the
      // accessibility tree. `inert` is deliberately used instead of rewriting vendor focus
      // controls or touching Cart behavior.
      if (!hasAttribute(drawer, 'inert')) drawer.setAttribute('inert', '');
      if (drawer.getAttribute('aria-modal') !== 'false') drawer.setAttribute('aria-modal', 'false');
      if (drawer.getAttribute('aria-label') !== cartLabel) drawer.setAttribute('aria-label', cartLabel);
      if (drawer.getAttribute('aria-hidden') !== 'true') drawer.setAttribute('aria-hidden', 'true');
    };

    const getFocusable = (drawer) => {
      if (!drawer || typeof drawer.querySelectorAll !== 'function') return [];
      return Array.from(drawer.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => (
        !element.hidden && element.getAttribute('aria-hidden') !== 'true'
      ));
    };

    const isElement = (node) => Boolean(node && typeof node.matches === 'function');

    const isKnownStructure = (node, root) => (
      isElement(node) && (
        node === root || STRUCTURE_SELECTORS.some((selector) => node.matches(selector))
      )
    );

    const nodeContainsKnownStructure = (node, root) => {
      if (!isElement(node)) return false;
      return isKnownStructure(node, root) || STRUCTURE_SELECTORS.some((selector) => (
        typeof node.querySelector === 'function' && Boolean(node.querySelector(selector))
      ));
    };

    const rootMutationNeedsReconcile = (records, root) => records.some((record) => {
      if (record.type === 'attributes') {
        return RECONCILE_ATTRIBUTES.has(record.attributeName) && (
          record.target === currentDrawer ||
          record.target === currentClose ||
          isKnownStructure(record.target, root)
        );
      }
      if (record.type !== 'childList') return false;
      if (isKnownStructure(record.target, root)) return true;
      return Array.from(record.addedNodes || []).some((node) => nodeContainsKnownStructure(node, root)) ||
        Array.from(record.removedNodes || []).some((node) => nodeContainsKnownStructure(node, root));
    });

    const stopRootObserver = () => {
      if (rootObserver) rootObserver.disconnect();
      rootObserver = null;
    };

    const stopDiscoveryObserver = () => {
      if (discoveryObserver) discoveryObserver.disconnect();
      discoveryObserver = null;
    };

    const queueReconcile = () => {
      if (!mounted || reconcileQueued) return;
      reconcileQueued = true;
      defer(() => {
        reconcileQueued = false;
        reconcile();
      });
    };

    const observeRoot = (root) => {
      stopRootObserver();
      if (!root || typeof window.MutationObserver !== 'function') return;
      rootObserver = new window.MutationObserver((records) => {
        if (!mounted || currentRoot !== root) return;
        if (rootMutationNeedsReconcile(records, root)) queueReconcile();
      });
      rootObserver.observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: Array.from(RECONCILE_ATTRIBUTES)
      });
    };

    const bindRoot = () => {
      const nextRoot = getRoot();
      if (nextRoot === currentRoot) return currentRoot;
      if (previousVisible) restoreTrigger();
      previousVisible = false;
      currentDrawer = null;
      removeCloseBinding();
      stopRootObserver();
      currentRoot = nextRoot;
      if (currentRoot) observeRoot(currentRoot);
      return currentRoot;
    };

    const ensureDiscoveryObserver = () => {
      if (
        !mounted ||
        discoveryObserver ||
        !document.body ||
        typeof window.MutationObserver !== 'function'
      ) return;
      discoveryObserver = new window.MutationObserver(() => {
        if (!mounted || !getRoot()) return;
        bindRoot();
        stopDiscoveryObserver();
        queueReconcile();
      });
      discoveryObserver.observe(document.body, { childList: true, subtree: true });
    };

    const bindDrawer = (root) => {
      const nextDrawer = getDrawer(root);
      if (nextDrawer === currentDrawer) return currentDrawer;
      if (previousVisible) restoreTrigger();
      previousVisible = false;
      removeCloseBinding();
      currentDrawer = nextDrawer;
      return currentDrawer;
    };

    const reconcile = () => {
      if (!mounted) return null;
      const root = bindRoot();
      if (!root) {
        ensureDiscoveryObserver();
        return null;
      }

      stopDiscoveryObserver();
      const drawer = bindDrawer(root);
      if (!drawer) return null;

      const visible = isVisible(drawer);
      const close = bindClose(drawer);
      if (!close) return drawer;
      updateDialog(drawer, visible);

      if (visible && !previousVisible) {
        rememberTrigger(drawer);
        scheduleFocus(close);
      } else if (!visible && previousVisible) {
        restoreTrigger();
      } else if (visible && !drawer.contains(document.activeElement)) {
        scheduleFocus(close);
      }
      previousVisible = visible;
      return drawer;
    };

    const onDocumentKeydown = (event) => {
      const drawer = reconcile();
      if (!drawer || !isVisible(drawer)) return;

      if (event.key === 'Escape') {
        const close = getClose(drawer);
        if (!close) return;
        event.preventDefault();
        event.stopPropagation();
        close.click();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = getFocusable(drawer);
      if (!focusable.length) {
        const close = getClose(drawer);
        if (close) {
          event.preventDefault();
          focusElement(close);
        }
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      const outside = !drawer.contains(active);
      if (event.shiftKey && (active === first || outside)) {
        event.preventDefault();
        focusElement(last);
      } else if (!event.shiftKey && (active === last || outside)) {
        event.preventDefault();
        focusElement(first);
      }
    };

    const onVendorVisibility = () => {
      if (mounted) queueReconcile();
    };

    const mount = () => {
      if (mounted) return;
      mounted = true;

      on(document, 'keydown', onDocumentKeydown, true);
      on(document, VISIBILITY_EVENT, onVendorVisibility, true);
      on(window, VISIBILITY_EVENT, onVendorVisibility, true);
      on(window, 'pagehide', destroy, { once: true });

      if (document.body) {
        reconcile();
      } else {
        on(document, 'DOMContentLoaded', () => {
          reconcile();
        }, { once: true });
      }
    };

    const destroy = () => {
      if (!mounted) return;
      mounted = false;
      reconcileQueued = false;
      cancelScheduledFocus();
      stopRootObserver();
      stopDiscoveryObserver();
      removeCloseBinding();
      cleanups.splice(0).forEach((cleanup) => cleanup());
      currentRoot = null;
      currentDrawer = null;
      previousVisible = false;
      lastTrigger = null;
      if (window[GLOBAL_KEY] === api) delete window[GLOBAL_KEY];
    };

    const api = { mount, destroy };
    return api;
  };

  const api = createController();
  window[GLOBAL_KEY] = api;
  api.mount();
})();
